<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import type { PropType, ComputedRef } from 'vue'
import { useStore } from '@nanostores/vue'
import { createGAEvent } from '@/assets/ts/customEvent'
import useProductSharing from '@/legacy/ts/useProductSharing'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import {
	kFormatter,
	MemberTypes,
	productSharingData,
	ProductSharingImageSize,
	getProductSharingUrl,
	getTimeAgoValue,
	checkExpressCheckoutEnable,
	productSharingPostPerPage,
} from '@/legacy/ts/productSharing'
import transformTextWithHashtag from '@/legacy/ts/checkHashtags'
import BsLoader from '@/legacy/components/Loader.vue'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import BsInfoSection from '@/legacy/components/InfoSection.vue'
import BsSideDrawer from '@/legacy/components/SideDrawer.vue'
import BsHowItWorksSkeleton from '@/legacy/components/HowItWorksSkeleton.vue'
import BsGallerySlideshow from '@/legacy/components/GallerySlideshow.vue'
import BsStickyBottomContainer from '@/legacy/components/StickyBottomContainer.vue'
import { InfoSectionTheme } from '@/types/InfoSectionLegacy'
import { createPostReminderHowItWorksView } from '@/legacy/ts/webComponents.js'
import { ButtonNormalTheme } from '@/types/ButtonLegacy'
import { getformatFloatPrice } from '@/assets/ts/currency'
import ProductSharingEmptyContent from '@/legacy/components/ProductSharingEmptyContent.vue'
import { getInfoBoxContent } from '@/legacy/ts/getMonth'
import BsNoticeBlock from '@/legacy/components/NoticeBlock.vue'
import { NoticeBlockIconType } from '@/types/NoticeBlockLegacy'
import injectStrict from '@/lib/inject'
import BsIconButton from '@/legacy/components/IconButton.vue'
import ProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import { fromPostId } from '@/store/productSharingStore'
import {
	instanceProductSharing,
	fetchProductSharingSettings as fetchProductSharingSettingsApi,
} from '@/assets/ts/apiManagement'
import { getSiteBaseUrl, getMcrRootUrl } from '@/lib/siteBaseUrl'
import { isRegionSupportBuyforyou } from '@/lib/checkService'

// in-app-solution
import { useAppOpener } from '@/lib/useAppOpener'

const eventInfo = {
	discover: {
		author: 'c_post_author_f_ps_discover_post',
		like: 'c_like_f_ps_discover_post',
		bookmark: 'c_bookmark_f_ps_discover_post',
		hashtag: 'c_hashtag_f_ps_discover_post',
		seeMore: 'c_see_more_f_ps_discover_post',
	},
	postDetail: {
		author: 'c_post_author_f_ps_post_detail',
		like: 'c_like_f_ps_post_detail',
		bookmark: 'c_bookmark_f_ps_post_detail',
		share: 'c_share_post_f_ps_post_detail',
		report: 'c_report_post_f_ps_post_detail',
		hashtag: 'c_hashtag_f_ps_post_detail',
		productCard: 'c_product_card_buy_f_ps_post_detail',
		openProductUrl: 'c_open_product_url_f_ps_post_detail',
		openBuyforyou: 'c_open_b4u_f_ps_post_detail',
		options: 'c_post_options_f_ps_post_detail',
	},
}

const props = defineProps({
	text: {
		type: Object,
	},
	detail: {
		type: null as unknown as PropType<any>,
		required: true,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	isTypeModal: {
		type: Boolean,
		default: false,
	},
	categories: {
		type: Array,
	},
	warehouses: {
		type: Object as PropType<any>,
	},
	supportWarehouses: {
		type: Array as PropType<any>,
	},
	interest: {
		type: Object as PropType<any>,
	},
	currencies: {
		type: Object as PropType<any>,
	},
	priceLocal: {
		type: String,
	},
	priceLocalPost: {
		type: String,
	},
	isShowContentBlock: {
		type: Boolean,
		default: false,
	},
	localCurrencyInfo: {
		type: Array as PropType<any>,
	},
	postListTextInfo: {
		type: Object,
	},
})

// in-app-solution
const { isInAppBrowser, openApp, openAppWithCheckout } = useAppOpener()

const fromPostIdGlobal = useStore(fromPostId)
const { isTabletPortrait: isShowDesktopLayout } = useBreakpoints()
const { isHasPSProfile, PSUsername } = useProductSharing()
const addMessage: (message: string) => void = injectStrict('addMessage')
const setPostListInfoProvide: ((payload: any) => void) | null = injectStrict(
	'setPostListInfoProvide',
	null,
)
const emit = defineEmits(['update-post', 'update-list'])

const shareCopyRef = ref<HTMLElement | null>(null)
const isShowDrawerRestrictions = ref(false)
const isWebComponentLoading = ref(false)
const infoSectionData = {
	theme: InfoSectionTheme.Info,
	text: props.text?.infoSection.text,
	arrowText: props.text?.infoSection.arrowText,
}
const sideDrawerTitleRestrictions = ref('')
const isExpressCheckoutEnable = checkExpressCheckoutEnable()
const currentInfoBoxContent = ref<{
	title: string
	description: string
} | null>(null)
const isRecommendShow = ref(true)
const isAcceptReturnRefund = ref(false)

const postImageList = computed(() => {
	if (props.isLoading || !props.detail || !Object.keys(props.detail).length)
		return []
	return props.detail.media.map((item: any) => ({
		id: item.id,
		type: item.type.replace('ps_', ''),
		content: getMedia(item),
		preview: item.type.includes('video') ? item.preview : null,
	}))
})

const posterAvatar = computed(() => {
	return props.detail && props.detail.userInfo && props.detail.userInfo.image
		? getPosterImage(props.detail.userInfo.image)
		: getVueAssetImagePath('avatar_2_48px@2x.png')
})

const isSupportExpressCheckout = computed(() => {
	return (props.detail.isExpressCheckout && isExpressCheckoutEnable) || false
})

const productInfo = computed(() => {
	if (!props.text || !props.detail || !props.detail.itemInfo) return []
	const productInfo = props.detail.itemInfo
	const hideConvertedPriceForExpressCheckout = isSupportExpressCheckout.value
		? `${getPricePost(true)}`
		: `${getPricePost(true)} (${props.priceLocalPost})`

	return [
		{
			title: props.text.title.itemName,
			value:
				isSupportExpressCheckout.value && productInfo.ecName
					? productInfo.ecName
					: productInfo.name,
			icon: 'product',
		},
		{
			title: isSupportExpressCheckout.value
				? props.text.title.unitPriceEc
				: props.text.title.unitPrice,
			value: hideConvertedPriceForExpressCheckout,
			icon: 'price-tag',
		},
		{
			title: props.text.title.discounted,
			value: productInfo.discounted
				? props.text.discounted[productInfo.discounted]
				: '',
			icon: 'saved-money',
		},
		{
			title: props.text.title.paymentMethod,
			value: productInfo.paymentMethod
				? props.text.paymentMethod[productInfo.paymentMethod]
				: '',
			icon: 'credit-card',
		},
	].filter(item => item.value !== '')
})

const shippingInfo = computed(() => {
	if (!props.text || !props.detail || !props.detail.itemInfo) return []
	const productInfo = props.detail.itemInfo
	return [
		{
			title: props.text.title.warehouse,
			value: warehouseName.value,
			icon: 'warehouse',
		},
		{
			title: props.text.title.location,
			value:
				productInfo.location &&
				window.BUYANDSHIP.settings.all_countries &&
				window.BUYANDSHIP.settings.all_countries[productInfo.location]
					? window.BUYANDSHIP.settings.all_countries[productInfo.location].name
					: '',
			icon: 'delivery',
		},
		{
			title: props.text.title.weight,
			value: `${productInfo.weight} ${props.text.unitPound}`,
			icon: 'weight',
		},
	].filter(item => item.value !== '')
})

const categoryName = computed(() => {
	const match = props.categories?.filter((item: any) =>
		item.includes(props.detail.itemInfo.category),
	)[0] as any
	return match && match[1]
})

const interestName = computed(() => {
	return props.interest && props.interest[props.detail.interest].name
})

const warehouseName = computed(() => {
	if (!props.warehouses || !props.warehouses[props.detail.itemInfo.warehouse])
		return null
	return props.warehouses[props.detail.itemInfo.warehouse].name
})

const postInfo = computed(() => {
	if (!props.detail || !props.detail.itemInfo) return []
	return [
		{
			title: props.text?.title.category,
			value: categoryName.value,
			icon: 'product-category',
		},
		{
			title: props.text?.title.interest,
			value: interestName.value,
			icon: 'post-category',
		},
	].filter(item => item.value !== '')
})

const currencySymbol = computed(() => {
	if (!props.currencies || !props.detail.itemInfo.warehouse) return null
	if (!props.warehouses?.[props.detail.itemInfo.warehouse]) return null
	if (
		props.warehouses?.[props.detail.itemInfo.warehouse].currency === undefined
	)
		return null
	const { currency } = props.warehouses[props.detail.itemInfo.warehouse]
	return props.currencies[currency.toLowerCase()].symbol
})

const postContent = computed(() => {
	if (!props.detail.content) return ''
	return transformTextWithHashtag(props.detail.content)
})

const isBuyforyouEnable = computed(() => {
	if (!props.supportWarehouses || !Object.keys(props.detail).length)
		return false
	const warehouseMatchIndex = props.supportWarehouses.findIndex(
		(v: any) => v === props.detail.itemInfo.warehouse,
	)
	return isRegionSupportBuyforyou.value && warehouseMatchIndex !== -1
})

const isDifferentCountry = computed(() => {
	if (props.detail.itemInfo) {
		return (
			window.BUYANDSHIP.settings.current_country !==
			props.detail.itemInfo.location
		)
	}
	return false
})

const eventParams = computed(() => {
	return {
		category: props.detail.productSlug || 'none',
		label: props.detail.id,
		value: 'public_detail',
	}
})

const eventParamsProductSlug = computed(() => {
	return {
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: 'public_detail',
		from: 'public_detail',
		from_post_id: currentFromPostId.value,
	}
})

const infoDisplay: ComputedRef<any> = computed(() => {
	return {
		product: productInfo,
		shipping: shippingInfo,
		post: postInfo,
	}
})

const likeCountDisplay = computed(() => {
	if (props.detail.likeCount) {
		return kFormatter(props.detail.likeCount, props.text).toString()
	}
	return '0'
})

const postService = computed(() => {
	if (isSupportExpressCheckout.value) return 'easybuy'
	if (isBuyforyouEnable.value) return 'b4u'
	return 'bns'
})

const isLocalCurrency = computed(() => {
	if (
		props.localCurrencyInfo &&
		props.localCurrencyInfo.length &&
		isSupportExpressCheckout.value
	) {
		const temp: any = props.localCurrencyInfo.filter(
			(item: any) => item.key === props.detail.warehouse,
		)
		if (temp.length) {
			return temp[0].isLocal
		}
		return false
	}
	return false
})

const localPricing = computed(() => {
	if (isLocalCurrency.value) {
		return getformatFloatPrice({
			price: props.detail.itemInfo.ecLocalPrice,
		})
	}
	return ''
})

const isMemberOfficial1 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Official1)
	}
	return false
})

const isMemberAssigned1 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Assigned1)
	}
	return false
})

const isMemberAssigned2 = computed(() => {
	if (props.detail?.userInfo?.memberTypes?.length) {
		return props.detail.userInfo.memberTypes.includes(MemberTypes.Assigned2)
	}
	return false
})

const isShowMemberBadges = computed(() => {
	return (
		isMemberOfficial1.value ||
		isMemberAssigned1.value ||
		isMemberAssigned2.value
	)
})

const memberTitle = computed(() => {
	if (isMemberOfficial1.value) {
		return props.text?.memberTypes.official1
	}
	if (isMemberAssigned1.value) {
		return props.text?.memberTypes.assigned1
	}
	if (isMemberAssigned2.value) {
		return props.text?.memberTypes.assigned2
	}
	return ''
})

const currentFromPostId = computed(() => {
	const fromPostId = new URLSearchParams(window.location.search).get(
		'fromPostId',
	)
	if (fromPostId) {
		return fromPostId
	}

	if (fromPostIdGlobal.value) {
		return fromPostIdGlobal.value
	}

	return ''
})

const linkRefund = computed(() => {
	return `${getSiteBaseUrl()}/protection/shopping/`
})

const imgAltText = computed(() => {
	return isSupportExpressCheckout.value ? props.detail.itemInfo.ecName : 'Image'
})

function action(name: string) {
	if (name === 'share') {
		shareMore()
		return
	}
	if (!productSharingData.isLogin && name !== 'share') {
		window.BsHeaderVm.showPanel('login')
		return
	}

	isHasPSProfile().then(isHasPSProfile => {
		if (!isHasPSProfile) {
			window.location.href = getProductSharingUrl('private-index')
			return
		}

		let prefix = ''
		let subfix = name
		if (name === 'like') {
			prefix = `${props.detail.isLiked ? '' : '/v2'}`
			if (props.detail.isLiked) {
				subfix = 'unlike'
			}
		} else if (name === 'bookmark') {
			prefix = `${props.detail.isBookmarked ? '' : '/v2'}`
			if (props.detail.isBookmarked) {
				subfix = 'unbookmark'
			}
		}
		const apiPath = `${prefix}/${subfix}`

		instanceProductSharing
			.post(apiPath, { post_id: props.detail.id })
			.then((res: any) => {
				const response = {
					isLiked: res.data.data.post.is_liked,
					isBookmarked: res.data.data.post.is_bookmarked,
					likeCount: res.data.data.post.like_count,
				}
				let payload
				if (name === 'like') {
					createGAEvent('ps_post_liked', {
						...eventParamsProductSlug.value,
						sequence_id: 'none',
					})
					sendEvent('like')
					sendEventActive()
					payload = {
						id: props.detail.id,
						param: 'isLiked',
						val: response.isLiked,
						likeCount: response.likeCount,
					}
				}
				if (name === 'bookmark') {
					createGAEvent('ps_post_bookmarked', eventParamsProductSlug.value)
					sendEvent('bookmark')
					sendEventActive()
					payload = {
						id: props.detail.id,
						param: 'isBookmarked',
						val: response.isBookmarked,
					}
				}

				if (payload) {
					emit('update-post', payload)
					if (setPostListInfoProvide) {
						setPostListInfoProvide(payload)
					}
				}

				if (name === 'report' || name === 'share') {
					const successMsg = props.text?.msg[name].success || ''
					addMessage(successMsg)
				}
				if (name === 'report') {
					sendEvent('report')
				}
			})
			.catch(() => {
				addMessage(props.text?.msg[name].fail)
			})
	})
}

function shareMore() {
	createGAEvent('ps_post_shared', {
		// duplicated column should be removed in the future
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: currentFromPostId.value,
		from_post_id: currentFromPostId.value,
	})
	sendEventActive()
	if (isShowDesktopLayout.value) {
		navigator.clipboard
			.writeText(shareCopyRef.value?.dataset.clipboardText || '')
			.then(() => addMessage(props.text?.msg.copy.success))
			.catch(() => addMessage(props.text?.msg.copy.failed))
	} else if (navigator.share) {
		navigator
			.share({
				title: 'Link for post',
				url: getProductSharingUrl('post', {
					id: props.detail.id,
					fromPostId: currentFromPostId.value,
				}),
			})
			.then(() => console.log('Successful share'))
			.catch(error => console.log('Error sharing', error))
	}
}

function viewUser() {
	sendEvent('author', {
		category: 'product_sharing',
		label: props.detail?.userInfo.name,
	})
	window.location.href = getProductSharingUrl('profile', {
		name: props.detail?.userInfo.name,
		fromPostId: props.detail.id,
	})
}

function getUrlQueryTransform(queryObj: any) {
	const urlParam: any[] = []

	Object.keys(queryObj).forEach(key => {
		urlParam.push(`${key}=${encodeURIComponent(queryObj[key])}`)
	})

	return urlParam
}

function getPosterImage(userImageObj: any) {
	if (
		!userImageObj ||
		!Object.keys(userImageObj).length ||
		!userImageObj.content
	)
		return null
	if (!userImageObj.has_resize) return userImageObj.content
	return userImageObj[ProductSharingImageSize.Profile]
}

function getMedia(mediaObj: any) {
	if (!mediaObj || !Object.keys(mediaObj).length || !mediaObj.content)
		return null
	if (mediaObj.type === 'ps_video' || !mediaObj.has_resize)
		return mediaObj.content
	return isShowDesktopLayout.value
		? mediaObj[ProductSharingImageSize.PostDesktop]
		: mediaObj[ProductSharingImageSize.PostMobile]
}

function getDateDifferenceValue(createTime: number) {
	return getTimeAgoValue(createTime, (props.text as any).timeago)
}

function sendEvent(
	name: keyof (typeof eventInfo)['postDetail'],
	params = {
		category: 'product_sharing',
		label: props.detail.id,
	},
) {
	const prefix = 'postDetail'
	if (eventInfo[prefix][name] === undefined) return
	createGAEvent(eventInfo[prefix][name], params)
}

function openProductUrl(isServiceAction = false) {
	createGAEvent(eventInfo.postDetail.openProductUrl, {
		// duplicated column should be removed in the future
		category: props.detail.productSlug || 'none',
		product_slug: props.detail.productSlug || 'none',
		label: props.detail.id,
		post_id: props.detail.id,
		value: isServiceAction ? 'primary_button' : 'product_info',
		from: isServiceAction ? 'primary_button' : 'product_info',
		from_post_id: currentFromPostId.value,
	})
	window.open(props.detail.itemInfo.url, '_blank')
}

function showDrawerRestrictions() {
	isShowDrawerRestrictions.value = true
}

function interceptInAppRequest() {
	if (isInAppBrowser()) {
		if (isSupportExpressCheckout.value) {
			openAppWithCheckout()
		} else {
			openApp()
		}
		return true
	}

	return false
}

function postServiceAction() {
	if (interceptInAppRequest()) {
		return
	}
	if (isSupportExpressCheckout.value) {
		createGAEvent('c_ec_f_ps_post_detail', {
			// duplicated column should be removed in the future
			category: currentFromPostId.value,
			from_post_id: currentFromPostId.value,
			label: props.detail.id,
			post_id: props.detail.id,
			value: props.detail.productSlug || 'none',
			product_slug: props.detail.productSlug || 'none',
		})
		let fromPostIdQuery = ''
		if (currentFromPostId.value) {
			if (import.meta.env.PROD) {
				fromPostIdQuery = `?fromPostId=${currentFromPostId.value}`
			} else {
				fromPostIdQuery = `&fromPostId=${currentFromPostId.value}`
			}
		}
		const link = `${getProductSharingUrl('express-checkout', {
			id: props.detail.id,
		})}${fromPostIdQuery}`
		window.open(link, '_blank')
		return
	}
	if (isBuyforyouEnable.value) {
		createGAEvent(eventInfo.postDetail.openBuyforyou, {
			// duplicated column should be removed in the future
			category: 'product_sharing',
			label: props.detail.id,
			post_id: props.detail.id,
			value: currentFromPostId.value || '',
			from_post_id: currentFromPostId.value || '',
		})
		if (!productSharingData.isLogin) {
			window.BsHeaderVm.showPanel('login')
			return
		}
		createGAEvent(eventInfo.postDetail.openBuyforyou, eventParams.value)
		const query = getUrlQueryTransform({
			default: 'true',
			warehouseId: props.detail.itemInfo.warehouse,
			itemName: props.detail.itemInfo.name,
			price: props.detail.itemInfo.unitPrice,
			link: props.detail.itemInfo.url,
			postId: props.detail.id,
		})
		const urlPrefix = `/buyforyou/new?${query.join('&')}`
		window.open(`${getMcrRootUrl()}${urlPrefix}`, '_blank')
		return
	}
	openProductUrl(true)
}

function goBack() {
	window.history.back()
}

function getPricePost(isItemInfo = false) {
	const price =
		isSupportExpressCheckout.value && !isItemInfo
			? props.detail.itemInfo.ecPrice
			: props.detail.itemInfo.unitPrice
	return `${currencySymbol.value} ${Number(price).toLocaleString('en')}`
}

function sendEventActive() {
	createGAEvent('ps_active_user')
}

function setIsRecommendShow(value: boolean) {
	isRecommendShow.value = value
}

function setInfoBox() {
	currentInfoBoxContent.value = getInfoBoxContent({
		fulfillmentMode: props.detail.ecFulfillmentMode,
		fulfillmentRange: props.detail.ecFulfillmentRange,
		fulfillmentMonth: props.detail.ecFulfillmentMonth,
		fulfillmentMaxOrderPerUser: props.detail.ecFulfillmentMaxOrderPerUser,
		fulfillmentMaxQuantity: props.detail.ecFulfillmentMaxQuantity,
		isOutOfStock: false,
		text: props.text,
	})
}

async function fetchProductSharingSettings() {
	try {
		const data = await fetchProductSharingSettingsApi()
		isAcceptReturnRefund.value = data.isAcceptReturnRefund
	} catch (error) {
		console.log(error)
	}
}

watch(
	() => isShowDrawerRestrictions.value,
	value => {
		if (value) {
			isWebComponentLoading.value = true
			const webcomponent: any = document.createElement(
				'web-component-post-reminder-how-it-works-view',
			)
			if (webcomponent.promise === undefined) {
				return
			}

			webcomponent.promise.then((title: string) => {
				isWebComponentLoading.value = false
				sideDrawerTitleRestrictions.value = title
			})
		}
	},
)

watch(
	() => props.isLoading,
	value => {
		if (!value) {
			setInfoBox()
			if (
				!window.BUYANDSHIP.settings.user_onboarded &&
				postService.value !== 'easybuy'
			) {
				window.BsHeaderVm.setTypeSuccessDialog(postService.value)
			}
			if (
				window.BsHeaderVm.isNeedTriggerActionButton &&
				postService.value !== 'easybuy'
			) {
				postServiceAction()
			}
		}
	},
)

fetchProductSharingSettings()

onMounted(() => {
	createPostReminderHowItWorksView()
})
</script>

<template>
	<div
		:class="[
			{ 'bs-product-sharing-post-card': !isShowContentBlock },
			{
				'bs-product-sharing-post-card--max':
					!isShowContentBlock && isShowDesktopLayout && isTypeModal,
			},
		]"
	>
		<BsLoader v-if="isLoading"></BsLoader>
		<ProductSharingEmptyContent
			v-if="!isLoading && isShowContentBlock"
			:text="text"
		></ProductSharingEmptyContent>
		<div
			class="bs-product-sharing-post-card__sticky-wrapper"
			:class="{
				'bs-product-sharing-post-card__sticky-wrapper--padding': !isTypeModal,
			}"
		>
			<template
				v-if="
					!isLoading && !isShowContentBlock && detail && isShowDesktopLayout
				"
			>
				<div class="bs-product-sharing-post-card__main">
					<BsGallerySlideshow
						:mediaList="postImageList"
						:textClose="text?.gallery.close"
						:imgAltText="imgAltText"
					/>
					<div class="bs-product-sharing-post-card__action">
						<span class="bs-product-sharing-post-card__action__item">
							<BsButtonNormal
								v-if="likeCountDisplay !== '0'"
								:theme="ButtonNormalTheme.Social"
								:text="likeCountDisplay"
								:action="() => action('like')"
							>
								<template #leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											:class="[
												'bs-icon',
												detail.isLiked
													? 'bs-icon--like--fill'
													: 'bs-icon--like',
											]"
										></i>
									</span>
								</template>
							</BsButtonNormal>
							<BsButtonNormal
								v-else
								:text="''"
								:theme="ButtonNormalTheme.Social"
								:action="() => action('like')"
							>
								<template #leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											:class="[
												'bs-icon',
												detail.isLiked
													? 'bs-icon--like--fill'
													: 'bs-icon--like',
											]"
										></i>
									</span>
								</template>
							</BsButtonNormal>
						</span>
						<span class="bs-product-sharing-post-card__action__item">
							<BsButtonNormal
								:theme="ButtonNormalTheme.Social"
								:text="text?.button.share"
								:action="() => action('share')"
							>
								<template v-slot:leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											class="bs-icon bs-icon--share"
											ref="shareCopyRef"
											:data-clipboard-text="
												getProductSharingUrl('share-post', { id: detail.id })
											"
										></i>
									</span>
								</template>
							</BsButtonNormal>
							<BsButtonNormal
								:theme="ButtonNormalTheme.Social"
								:text="text?.button.bookmark"
								:action="() => action('bookmark')"
							>
								<template #leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											:class="[
												'bs-icon',
												detail.isBookmarked
													? 'bs-icon--bookmark--fill'
													: 'bs-icon--bookmark',
											]"
										></i>
									</span>
								</template>
							</BsButtonNormal>
						</span>
					</div>
					<div
						class="bs-product-sharing-post-card__info bs-product-sharing-post-card__info__post"
						v-if="!isLoading"
					>
						<div @click="viewUser" class="bs-product-sharing-post-card__poster">
							<div class="bs-product-sharing-post-card__poster__avatar">
								<img
									class="bs-product-sharing-post-card__poster__avatar__img"
									:src="posterAvatar"
									alt="Avatar"
									loading="lazy"
								/>
							</div>
							<div class="bs-product-sharing-post-card__poster__info">
								<div class="bs-product-sharing-post-card__poster__info__name">
									<div
										class="bs-product-sharing-post-card__poster__info__name--text"
									>
										{{ detail.userInfo.name }}
									</div>
									<div
										class="bs-product-sharing-post-card__poster__info__name--member"
										v-if="isShowMemberBadges"
									>
										<span
											class="bs-icon-member-official-1"
											v-if="isMemberOfficial1"
										></span>
										<span
											class="bs-icon-member-assigned-1"
											v-if="isMemberAssigned1"
										></span>
										<span
											class="bs-icon-member-assigned-2"
											v-if="isMemberAssigned2"
										></span>
										<div
											class="bs-product-sharing-post-card__poster__info__name--member__title"
											v-if="memberTitle"
										>
											{{ memberTitle }}
										</div>
									</div>
								</div>
								<div class="bs-product-sharing-post-card__poster__info__other">
									{{ getDateDifferenceValue(detail.publishDate) }}
								</div>
							</div>
						</div>
						<div class="bs-product-sharing-post-card__post">
							<div v-if="!isSupportExpressCheckout">
								<h1 class="bs-product-sharing-post-card__post__title">
									{{ detail.title }}
								</h1>
							</div>
							<div v-else>
								<div class="bs-product-sharing-post-card__post__title">
									{{ detail.title }}
								</div>
							</div>

							<div class="bs-product-sharing-post-card__post__content">
								<div
									class="bs-product-sharing-post-card__post__content__text"
									v-html="postContent"
								></div>
							</div>
						</div>
					</div>
					<div
						v-for="(info, key) in infoDisplay"
						:key="key"
						class="bs-product-sharing-post-card__info"
					>
						<h2 class="bs-product-sharing-post-card__info__title">
							{{ text?.title[`${key}`] }}
							<BsButtonOutline
								v-if="(key as any) === 'product'"
								:text="text?.button.openItemUrl"
								:action="openProductUrl"
							>
								<template #trailing>
									<span class="bs-product-sharing-post-card__action__item">
										<i class="bs-icon bs-icon--share-open"></i>
									</span>
								</template>
							</BsButtonOutline>
						</h2>
						<div
							v-for="item in info.value"
							:key="item.icon"
							class="bs-product-sharing-post-card__info__item"
						>
							<i :class="`bs-icon bs-icon--${item.icon}`"></i>
							<div class="bs-product-sharing-post-card__info__item__content">
								<h3
									class="bs-product-sharing-post-card__info__item__content__title"
								>
									{{ item.title }}
								</h3>
								<span
									class="bs-product-sharing-post-card__info__item__content__value"
								>
									{{ item.value }}
								</span>
							</div>
						</div>
					</div>
					<div class="bs-product-sharing-post-card__bottom">
						<div
							class="bs-product-sharing-post-card__bottom__report"
							@click="action('report')"
						>
							<i class="bs-icon bs-icon--report"></i>
							<span class="bs-product-sharing-post-card__bottom__report__text">
								{{ text?.button.report }}
							</span>
						</div>
						<a
							class="bs-product-sharing-post-card__bottom__refund"
							v-if="isAcceptReturnRefund"
							:href="linkRefund"
							target="_blank"
						>
							<i class="bs-icon bs-icon--refund"></i>
							<span class="bs-product-sharing-post-card__bottom__refund__text">
								{{ text?.button.refund }}
							</span>
						</a>
					</div>
				</div>
				<div
					v-if="!isLoading && detail"
					:class="[
						'bs-product-sharing-post-card__side',
						{ 'bs-product-sharing-post-card__side--padding': !isTypeModal },
					]"
				>
					<div
						v-if="isSupportExpressCheckout"
						class="bs-product-sharing-post-card__service"
					>
						<i :class="`bs-icon bs-icon--${postService}`"></i>
						<div class="bs-product-sharing-post-card__service__content">
							<span
								class="bs-product-sharing-post-card__service__content__title"
							>
								{{ text?.service.title[postService] }}
							</span>
						</div>
					</div>
					<div
						v-if="isSupportExpressCheckout"
						class="bs-product-sharing-post-card__product"
					>
						<h1 class="bs-product-sharing-post-card__product__name">
							{{
								isSupportExpressCheckout
									? detail.itemInfo.ecName
									: detail.itemInfo.name
							}}
						</h1>
						<div
							:class="[
								'bs-product-sharing-post-card__product__price',
								{ adjustment: isLocalCurrency },
							]"
						>
							{{ isLocalCurrency ? localPricing : getPricePost() }}
						</div>
						<div
							class="bs-product-sharing-post-card__product__price__local"
							v-if="!isLocalCurrency"
						>
							{{ priceLocal }}
						</div>
					</div>
					<div
						:class="[
							'bs-product-sharing-post-card__catch',
							{
								'bs-product-sharing-post-card__catch--reverse':
									!isSupportExpressCheckout,
							},
						]"
					>
						<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
						<BsButtonNormal
							id="success-dialog-primary-button"
							class="bs-product-sharing-post-card__product__button"
							:isRoundEdge="false"
							:text="text?.service.action[postService]"
							:action="postServiceAction"
						>
							<template #trailing>
								<span
									v-if="!isSupportExpressCheckout"
									class="bs-product-sharing-post-card__action__item"
								>
									<i class="bs-icon bs-icon--share-open"></i>
								</span>
							</template>
						</BsButtonNormal>
						<BsNoticeBlock
							v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
							class="bs-product-sharing__notice-block desktop"
							:icon="NoticeBlockIconType.Info"
						>
							<template v-slot:title>
								{{ currentInfoBoxContent.title }}
							</template>
							<template v-slot:description>
								<div v-html="currentInfoBoxContent.description"></div>
							</template>
						</BsNoticeBlock>
						<div class="bs-product-sharing-post-card__promote">
							<div class="bs-product-sharing-post-card__promote__title">
								{{ text?.promote.title[postService] }}
							</div>
							<div class="bs-product-sharing-post-card__promote__desc">
								{{ text?.promote.content[postService] }}
							</div>
						</div>
					</div>
					<div v-if="isDifferentCountry" class="bs-info-section--container">
						<BsInfoSection
							:theme="infoSectionData.theme"
							:content="infoSectionData.text"
							:arrowText="infoSectionData.arrowText"
							:action="showDrawerRestrictions"
						/>
					</div>
				</div>
			</template>
			<template
				v-if="
					!isLoading && !isShowContentBlock && detail && !isShowDesktopLayout
				"
			>
				<div
					:class="[
						'bs-product-sharing-post-card__nav',
						{ 'bs-product-sharing-post-card__nav--between': isTypeModal },
					]"
				>
					<span v-if="isTypeModal" @click="goBack">
						<i class="bs-icon bs-icon--back--circle"></i>
					</span>
					<div class="bs-product-sharing-post-card__action__item">
						<span class="bs-member-icon-button" @click="action('share')">
							<i
								class="bs-icon bs-icon--share--circle"
								ref="shareCopyRef"
								:data-clipboard-text="
									getProductSharingUrl('share-post', { id: detail.id })
								"
							></i>
						</span>
						<span class="bs-member-icon-button" @click="action('bookmark')">
							<i
								:class="[
									'bs-icon',
									detail.isBookmarked
										? 'bs-icon--bookmark--circle--fill'
										: 'bs-icon--bookmark--circle',
								]"
							></i>
						</span>
					</div>
				</div>
				<BsGallerySlideshow
					:mediaList="postImageList"
					:textClose="text?.gallery.close"
					:imgAltText="imgAltText"
				/>
				<div v-if="detail" class="bs-product-sharing-post-card__main">
					<div
						v-if="isSupportExpressCheckout"
						class="bs-product-sharing-post-card__service"
					>
						<i :class="`bs-icon bs-icon--${postService}`"></i>
						<div class="bs-product-sharing-post-card__service__content">
							<span
								class="bs-product-sharing-post-card__service__content__title"
							>
								{{ text?.service.title[postService] }}
							</span>
						</div>
					</div>
					<h1
						v-if="isSupportExpressCheckout"
						class="bs-product-sharing-post-card__product__name"
					>
						{{ detail.itemInfo.ecName }}
					</h1>
					<BsNoticeBlock
						v-if="isSupportExpressCheckout && currentInfoBoxContent?.title"
						class="bs-product-sharing__notice-block mobile"
						:icon="NoticeBlockIconType.Info"
					>
						<template v-slot:title>{{ currentInfoBoxContent.title }}</template>
						<template v-slot:description>
							<div v-html="currentInfoBoxContent.description"></div>
						</template>
					</BsNoticeBlock>
					<div
						class="bs-product-sharing-post-card__info bs-product-sharing-post-card__info__post"
						v-if="!isLoading"
					>
						<div @click="viewUser" class="bs-product-sharing-post-card__poster">
							<div class="bs-product-sharing-post-card__poster__avatar">
								<img
									class="bs-product-sharing-post-card__poster__avatar__img"
									:src="posterAvatar"
									alt="avatar"
									loading="lazy"
								/>
							</div>
							<div class="bs-product-sharing-post-card__poster__info">
								<div class="bs-product-sharing-post-card__poster__info__name">
									<div
										class="bs-product-sharing-post-card__poster__info__name--text"
									>
										{{ detail.userInfo.name }}
									</div>
									<div
										class="bs-product-sharing-post-card__poster__info__name--member"
										v-if="isShowMemberBadges"
									>
										<span
											class="bs-icon-member-official-1"
											v-if="isMemberOfficial1"
										></span>
										<span
											class="bs-icon-member-assigned-1"
											v-if="isMemberAssigned1"
										></span>
										<span
											class="bs-icon-member-assigned-2"
											v-if="isMemberAssigned2"
										></span>
									</div>
								</div>
								<div class="bs-product-sharing-post-card__poster__info__other">
									{{ getDateDifferenceValue(detail.publishDate) }}
								</div>
							</div>
						</div>
						<div class="bs-product-sharing-post-card__post">
							<div v-if="!isSupportExpressCheckout">
								<h1 class="bs-product-sharing-post-card__post__title">
									{{ detail.title }}
								</h1>
							</div>
							<div v-else>
								<div class="bs-product-sharing-post-card__post__title">
									{{ detail.title }}
								</div>
							</div>

							<div class="bs-product-sharing-post-card__post__content">
								<div
									class="bs-product-sharing-post-card__post__content__text"
									v-html="postContent"
								></div>
							</div>
						</div>
					</div>
					<div class="bs-product-sharing-post-card__action">
						<span class="bs-product-sharing-post-card__action__item">
							<BsButtonNormal
								v-if="likeCountDisplay !== '0'"
								:theme="ButtonNormalTheme.Social"
								:text="likeCountDisplay"
								:action="() => action('like')"
							>
								<template #leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											:class="[
												'bs-icon',
												detail.isLiked
													? 'bs-icon--like--fill'
													: 'bs-icon--like',
											]"
										></i>
									</span>
								</template>
							</BsButtonNormal>
							<BsButtonNormal
								v-else
								:theme="ButtonNormalTheme.Social"
								:text="''"
								:action="() => action('like')"
							>
								<template #leading>
									<span class="bs-product-sharing-post-card__action__item">
										<i
											:class="[
												'bs-icon',
												detail.isLiked
													? 'bs-icon--like--fill'
													: 'bs-icon--like',
											]"
										></i>
									</span>
								</template>
							</BsButtonNormal>
						</span>
					</div>
					<div v-for="(info, key) in infoDisplay" :key="key">
						<div class="bs-product-sharing-post-card__info">
							<h2 class="bs-product-sharing-post-card__info__title">
								{{ text?.title[`${key}`] }}
							</h2>
							<div
								v-for="item in info.value"
								:key="item.icon"
								class="bs-product-sharing-post-card__info__item"
							>
								<i :class="`bs-icon bs-icon--${item.icon}`"></i>
								<div class="bs-product-sharing-post-card__info__item__content">
									<h3
										class="bs-product-sharing-post-card__info__item__content__title"
									>
										{{ item.title }}
									</h3>
									<span
										class="bs-product-sharing-post-card__info__item__content__value"
									>
										{{ item.value }}
									</span>
								</div>
							</div>
							<BsButtonOutline
								v-if="(key as any) === 'product'"
								:text="text?.button.openItemUrl"
								:action="openProductUrl"
							>
								<template #trailing>
									<BsIconButton
										class="bs-product-sharing-post-card__action__item"
										:size="20"
									>
										<i class="bs-icon bs-icon--share-open"></i>
									</BsIconButton>
								</template>
							</BsButtonOutline>
						</div>
						<div
							v-if="(key as any) === 'product' && isDifferentCountry"
							class="bs-info-section--container"
						>
							<BsInfoSection
								:theme="infoSectionData.theme"
								:content="infoSectionData.text"
								:arrowText="infoSectionData.arrowText"
								:action="showDrawerRestrictions"
							/>
						</div>
					</div>
					<div class="bs-product-sharing-post-card__bottom">
						<div
							class="bs-product-sharing-post-card__bottom__report"
							@click="action('report')"
						>
							<i class="bs-icon bs-icon--report"></i>
							<span class="bs-product-sharing-post-card__bottom__report__text">
								{{ text?.button.report }}
							</span>
						</div>
						<a
							class="bs-product-sharing-post-card__bottom__refund"
							v-if="isAcceptReturnRefund"
							:href="linkRefund"
							target="_blank"
						>
							<i class="bs-icon bs-icon--refund"></i>
							<span class="bs-product-sharing-post-card__bottom__refund__text">
								{{ text?.button.refund }}
							</span>
						</a>
					</div>
					<template v-if="!isLoading && isRecommendShow">
						<div class="bs-product-sharing-post-card__recommend">
							<div class="bs-product-sharing-post-card__recommend__title">
								{{ text?.recommend.title }}
							</div>
							<ProductSharingPostList
								:text="postListTextInfo"
								:isRecommend="true"
								:columnCount="2"
								:currentPostId="detail.id"
								:postPerPage="productSharingPostPerPage.recommend"
								:isTypeModal="isTypeModal"
								@setIsRecommendShow="setIsRecommendShow"
							></ProductSharingPostList>
						</div>
					</template>
				</div>
				<BsStickyBottomContainer class="bs-product-sharing-post-card__sticky">
					<div
						v-if="postService === 'easybuy'"
						class="bs-product-sharing-post-card__product"
					>
						<div class="bs-product-sharing-post-card__product__price">
							{{ isLocalCurrency ? localPricing : getPricePost() }}
						</div>
						<div
							class="bs-product-sharing-post-card__product__price__local"
							v-if="!isLocalCurrency"
						>
							{{ priceLocal }}
						</div>
					</div>
					<!-- Be careful when change id name here, might affect AuthSuccess.vue -->
					<BsButtonNormal
						id="success-dialog-primary-button"
						class="bs-product-sharing-post-card__product__button"
						:isRoundEdge="false"
						:text="text?.service.action[postService]"
						:action="postServiceAction"
					>
						<template #trailing>
							<span
								v-if="!isSupportExpressCheckout"
								class="bs-product-sharing-post-card__action__item"
							>
								<i class="bs-icon bs-icon--share-open"></i>
							</span>
						</template>
					</BsButtonNormal>
				</BsStickyBottomContainer>
			</template>
		</div>
		<template
			v-if="
				!isLoading &&
				!isShowContentBlock &&
				isShowDesktopLayout &&
				isRecommendShow
			"
		>
			<div class="bs-product-sharing-post-card__recommend desktop">
				<div class="bs-product-sharing-post-card__recommend__title">
					{{ text?.recommend.title }}
				</div>
				<ProductSharingPostList
					:text="postListTextInfo"
					:isRecommend="true"
					:currentPostId="detail.id"
					:columnCount="5"
					:postPerPage="productSharingPostPerPage.recommend"
					:isTypeModal="isTypeModal"
					@setIsRecommendShow="setIsRecommendShow"
				></ProductSharingPostList>
			</div>
		</template>
		<Teleport to="body">
			<BsSideDrawer
				v-model="isShowDrawerRestrictions"
				:title="sideDrawerTitleRestrictions"
				:isShowTrailing="true"
			>
				<BsHowItWorksSkeleton v-if="isWebComponentLoading" />
				<div v-show="!isWebComponentLoading">
					<web-component-post-reminder-how-it-works-view v-pre />
				</div>
			</BsSideDrawer>
		</Teleport>
	</div>
</template>
